import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, X, AlertTriangle, LogIn, LogOut, Camera, CameraOff, Shuffle, ArrowLeft, DoorOpen, Maximize2 } from "lucide-react";
import { SiteHeader, PageFooter, DoItSignature, UpvMark } from "@/components/brand/Brand";
import { ScanViewport } from "@/components/scan/ScanViewport";
import { ScanResultOverlay } from "@/components/scan/ScanResultOverlay";
import { KpiNumber } from "@/components/common/KpiNumber";
import { useDemo } from "@/lib/demo/store";
import { useScanFlow } from "@/lib/demo/useScanFlow";
import { buildDashboardView, isInside } from "@/lib/demo/analytics";
import { CHECKPOINTS } from "@/lib/demo/seed";
import { fullName, shortName, clockNow } from "@/lib/demo/utils";
import { useI18n } from "@/lib/i18n/i18n";

const DURATION = 1500;

function toOverlay(r, mode, t) {
  if (!r) return null;
  const p = r.participant;
  if (r.status === "invalid") return { tone: "error", icon: X, title: t("QR NO VÁLIDO"), note: t("Código no reconocido por el sistema"), time: clockNow(), testId: "access-result-invalid" };
  if (r.status === "already_inside") return { tone: "warning", icon: AlertTriangle, title: t("YA DENTRO DEL RECINTO"), name: fullName(p), note: t("Entrada registrada previamente · debe registrar su salida"), time: clockNow(), testId: "access-result-already-inside" };
  if (r.status === "not_inside") return { tone: "warning", icon: AlertTriangle, title: t("SIN ENTRADA REGISTRADA"), name: fullName(p), note: t("Este participante no figura dentro del recinto"), time: clockNow(), testId: "access-result-not-inside" };
  return mode === "entry"
    ? { tone: "success", icon: Check, title: t("✓ ACCESO REGISTRADO"), name: fullName(p).toUpperCase(), lines: [t(p.titulacion)], time: r.event.time, testId: "access-result-entry" }
    : { tone: "exit", icon: Check, title: t("✓ SALIDA REGISTRADA"), name: shortName(p).toUpperCase(), lines: [t(p.titulacion)], time: r.event.time, testId: "access-result-exit" };
}

const AccessSetup = ({ onStart }) => {
  const [cp, setCp] = useState(CHECKPOINTS[0]);
  const [mode, setMode] = useState("entry");
  const pick = (c) => { setCp(c); setMode(c.mode); };
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-slate-50 pb-28" data-testid="access-setup">
      <SiteHeader />
      <main className="mx-auto max-w-4xl px-5 py-14 lg:px-10">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-upv">{t("Operativa de puertas")}</p>
        <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">{t("CONTROL DE ACCESO")}</h1>
        <p className="mt-3 text-slate-600">{t("Entradas y salidas ilimitadas. Cada lectura queda registrada como evento con punto, tipo y hora.")}</p>
        <h2 className="mt-12 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{t("Seleccionar punto")}</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {CHECKPOINTS.map((c) => (
            <button key={c.id} onClick={() => pick(c)} className={`flex items-center gap-4 rounded-2xl border-2 p-5 text-left transition-colors ${cp.id === c.id ? "border-upv bg-white" : "border-slate-200 bg-white hover:border-slate-400"}`} data-testid={`access-checkpoint-${c.id}`}>
              <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${cp.id === c.id ? "bg-upv text-white" : "bg-slate-100 text-slate-500"}`}><DoorOpen className="h-5 w-5" /></span>
              <span className="font-display text-lg font-bold">{t(c.name)}</span>
            </button>
          ))}
        </div>
        <h2 className="mt-10 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{t("Seleccionar modo")}</h2>
        <div className="mt-4 grid grid-cols-2 gap-3">
          {[["entry", "ENTRADA", LogIn], ["exit", "SALIDA", LogOut]].map(([m, l, Icon]) => (
            <button key={m} onClick={() => setMode(m)} className={`flex h-20 items-center justify-center gap-3 rounded-2xl border-2 font-display text-xl font-extrabold tracking-wider transition-colors ${mode === m ? (m === "entry" ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-900 bg-slate-900 text-white") : "border-slate-200 bg-white text-slate-500 hover:border-slate-400"}`} data-testid={`access-mode-${m}`}>
              <Icon className="h-5 w-5" /> {t(l)}
            </button>
          ))}
        </div>
        <button onClick={() => onStart({ checkpoint: cp, mode })} className="mt-10 flex h-16 w-full items-center justify-center gap-3 rounded-2xl bg-upv font-display text-lg font-bold tracking-wider text-white transition-colors hover:bg-upv-dark" data-testid="access-start-button">
          <Maximize2 className="h-5 w-5" /> {t("INICIAR ESCÁNER")}
        </button>
      </main>
      <PageFooter />
    </div>
  );
};

const AccessScanner = ({ config, onExit }) => {
  const { state, recordAccess, activeParticipant, navHidden } = useDemo();
  const { checkpoint, mode } = config;
  const [camera, setCamera] = useState(false);
  const { t } = useI18n();
  const process = useCallback((token) => recordAccess(token, checkpoint.id, mode), [recordAccess, checkpoint.id, mode]);
  const { phase, result, trigger } = useScanFlow(process, DURATION);
  const inside = useMemo(() => buildDashboardView("live", state).kpis.inside, [state]);
  const reads = state.accessEvents.filter((e) => e.checkpointId === checkpoint.id && e.id.startsWith("live-")).slice(-5).reverse();
  const byId = Object.fromEntries(state.participants.map((p) => [p.id, p]));

  const simulateOther = () => {
    const pool = state.participants.filter((p) => p.id !== activeParticipant.id && (mode === "entry" ? !isInside(state, p.id) : isInside(state, p.id)));
    const p = pool[Math.floor(Math.random() * pool.length)] || state.participants[1];
    trigger(p.token);
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-white" data-testid="access-scanner">
      <header className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-4 lg:px-10">
        <div className="flex items-center gap-4">
          <button onClick={onExit} className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition-colors hover:border-slate-900" data-testid="access-exit-button"><ArrowLeft className="h-4 w-4" /></button>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-400">{t("Control de acceso")}</p>
            <p className="font-display text-xl font-extrabold uppercase tracking-tight sm:text-2xl" data-testid="access-checkpoint-title">{t(checkpoint.name)}</p>
          </div>
        </div>
        <span className={`rounded-full px-4 py-2 font-display text-sm font-extrabold tracking-wider text-white ${mode === "entry" ? "bg-emerald-600" : "bg-slate-900"}`} data-testid="access-mode-badge">{t(mode === "entry" ? "ENTRADA" : "SALIDA")}</span>
        <span className="hidden lg:block"><UpvMark /></span>
      </header>
      <div className={`grid flex-1 gap-10 px-5 py-8 lg:grid-cols-[1fr_340px] lg:px-10 ${navHidden ? "pb-8" : "pb-24"}`}>
        <div className="flex flex-col items-center justify-center">
          <div className="w-full max-w-[420px]"><ScanViewport phase={phase} cameraOn={camera} onDecode={(t) => trigger(t, { fromCamera: true })} /></div>
          <p className="mt-8 text-center font-display text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="access-instruction">{t("MUESTRA TU CÓDIGO QR")}</p>
          <div className="mt-8 flex w-full max-w-[420px] flex-col gap-3">
            <button onClick={() => trigger(activeParticipant.token)} disabled={phase !== "idle"} className="flex h-16 items-center justify-center rounded-2xl bg-upv font-display text-lg font-bold tracking-wider text-white transition-colors hover:bg-upv-dark disabled:opacity-60" data-testid="access-simulate-button">{t("SIMULAR LECTURA QR")}</button>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={simulateOther} disabled={phase !== "idle"} className="flex h-12 items-center justify-center gap-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-900 disabled:opacity-60" data-testid="access-simulate-other-button"><Shuffle className="h-4 w-4" /> {t("Otro participante")}</button>
              <button onClick={() => setCamera((c) => !c)} className="flex h-12 items-center justify-center gap-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-900" data-testid="access-camera-toggle">{camera ? <CameraOff className="h-4 w-4" /> : <Camera className="h-4 w-4" />} {t(camera ? "Apagar cámara" : "Usar cámara")}</button>
            </div>
            <p className="text-center text-xs text-slate-400">{t("Simulación: lee la credencial de {name}", { name: fullName(activeParticipant) })}</p>
          </div>
        </div>
        <aside className="space-y-4">
          <div className="rounded-[24px] bg-slate-950 p-6 text-white">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/50">{t("Ahora en el recinto")}</p>
            <KpiNumber value={inside} testId="access-inside-count" className="mt-2 font-display text-5xl font-bold" />
          </div>
          <div className="rounded-[24px] border border-slate-200 p-6">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-400">{t("Últimas lecturas · este punto")}</p>
            {reads.length === 0 ? <p className="mt-3 text-sm text-slate-500">{t("Sin lecturas en esta sesión.")}</p> : (
              <ul className="mt-3 space-y-2">
                {reads.map((e) => (
                  <li key={e.id} className="flex items-center gap-3 text-sm" data-testid={`access-read-${e.id}`}>
                    <span className="font-mono font-bold">{e.time}</span>
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${e.type === "entry" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-700"}`}>{t(e.type === "entry" ? "ENTRADA" : "SALIDA")}</span>
                    <span className="truncate text-slate-600">{byId[e.participantId] ? fullName(byId[e.participantId]) : ""}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <DoItSignature className="text-center lg:text-left" />
        </aside>
      </div>
      <ScanResultOverlay data={phase === "result" ? toOverlay(result, mode, t) : null} duration={DURATION} />
    </div>
  );
};

export default function Acceso() {
  const [config, setConfig] = useState(null);
  useEffect(() => () => { if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {}); }, []);
  const start = (c) => { document.documentElement.requestFullscreen?.().catch(() => {}); setConfig(c); };
  const exit = () => { if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {}); setConfig(null); };
  return config ? <AccessScanner config={config} onExit={exit} /> : <AccessSetup onStart={start} />;
}
